import React, { useEffect, useState } from 'react'
import {
  setFocus,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { useNavigate } from 'react-router'

import UpNextCircleCountdown from './UpNextCircleCountdown'
import PassportBadge from '../PassportBadge'
import FocusableButtonTS from '../FocusableButton/FocusableButtonTS'
import { useAppSelector } from '../../hooks/useStore'
import { useVideoPlayerData } from '../VideoPlayer/VideoPlayerContext'
import { toHHMMSS } from '../../../../shared/helpers'
import { KeyTypeEnum } from '../../../../shared/hooks/useKeyDownHandler'
import tts from '../../lib/TTS'

function UpNext() {
  const { playerState, setIsUpNextVisible } = useVideoPlayerData()
  const timeRemaining = playerState.duration - playerState.currentProgress
  const videoDetails = useAppSelector((state) => state.content.videoDetails)
  const stationTitle = useAppSelector(
    (state) => state.station.short_common_name
  )
  const { ref } = useFocusable({
    isFocusBoundary: true,
    forceFocus: true,
    preferredChildFocusKey: 'up-next-thumbnail',
    saveLastFocusedChild: false,
  })
  const navigate = useNavigate()
  const isContentFullLengthAndReachedThreshold =
    videoDetails.contentType === 'full_length' && timeRemaining <= 30
  const isContentNotFullLengthAndReachedThreshold =
    videoDetails.contentType !== 'full_length' && timeRemaining <= 10
  const isTimeToShow =
    (playerState.upNextData !== null &&
      isContentFullLengthAndReachedThreshold) ||
    isContentNotFullLengthAndReachedThreshold
  const [isVisible, setIsVisible] = useState(false)
  const [upNextShown, setUpNextShown] = useState(false)

  if (isTimeToShow && !isVisible && !upNextShown) {
    setIsVisible(true)
    setUpNextShown(true)
    setIsUpNextVisible(true)
    setFocus('up-next-thumbnail')
  } else if (!isTimeToShow && upNextShown) {
    setUpNextShown(false)
  }

  useEffect(() => {
    if (!playerState.upNextData?.isUpNextVisible) {
      setIsVisible(false)
    }
  }, [playerState.upNextData?.isUpNextVisible])

  const handleUpNext = () => {
    navigate(
      `/video-${
        playerState.upNextData?.isMvod && !playerState.upNextData?.isPlayable
          ? 'details'
          : 'playback'
      }/${playerState.upNextData?.upNextUrl}`,
      { replace: true, state: { fromUpNext: true } }
    )
  }

  const handleDismiss = () => {
    setIsVisible(false)
    setIsUpNextVisible(false)
  }

  const handleDismissFocus = () => {
    tts.addTextToRead('Dismiss Up Next')
  }

  const handleUpNextFocus = () => {
    tts.addTextToRead(
      `Focused on Up Next Thumbnail. ${playerState.upNextData?.upNextShowTitle} is up next`
    )
  }

  return (
    <div
      className="up-next"
      ref={ref}
      style={{ display: isVisible ? 'block' : 'none' }}
    >
      <div className="up-next-info">
        <div className="up-next__data">
          <h4 className="up-next-in">
            Up Next in
            <span className="up-next-counter">{` ${
              toHHMMSS(timeRemaining).split(':')[1]
            }`}</span>
          </h4>

          <p className="up-next--text up-next__title">
            {playerState.upNextData?.upNextShowTitle}
          </p>
          <p className="up-next--text">
            {playerState.upNextData?.upNextSeasonNumber
              ? `S${playerState.upNextData?.upNextSeasonNumber}`
              : ''}
            {playerState.upNextData?.upNextEpisodeNumber
              ? `Ep${playerState.upNextData?.upNextEpisodeNumber}`
              : ''}
            | {playerState.upNextData?.upNextEpisodeTitle}
          </p>
          {playerState.upNextData?.isMvod &&
            !playerState.upNextData?.isPlayable && (
              <p className="up-next--text up-next__availability-info">{`Available with ${stationTitle} Passport`}</p>
            )}
          <FocusableButtonTS
            onEnterPress={handleDismiss}
            onFocus={handleDismissFocus}
            onArrowPress={(e) => {
              if (e === KeyTypeEnum.Right) {
                return true
              }
              return false
            }}
          >
            Dismiss
          </FocusableButtonTS>
        </div>
        <div className="up-next-thumbnail">
          <FocusableButtonTS
            onEnterPress={handleUpNext}
            onFocus={handleUpNextFocus}
            focusKey="up-next-thumbnail"
          >
            {playerState.upNextData?.isMvod && <PassportBadge sizePx={80} />}
            <img
              src={playerState.upNextData?.upNextImage}
              className="up-next-image"
            />
            <UpNextCircleCountdown
              timeRemaining={timeRemaining}
              length={videoDetails.contentType}
            />
          </FocusableButtonTS>
        </div>
      </div>
    </div>
  )
}

export default UpNext
